import axios from 'axios';

import config from '@/config';
import fetchWrapper from '@/services/fetchWrapper.service';
import { prepareImageForUpload } from '@/utils/prepareImageForUpload';

let userChatsUnreadSummaryRequest = null;

function contact(contactData, type) {
  return fetchWrapper.post(`${config.apis.api.url}/contact`, {
    body: {
      contact_name: contactData.contact_name.value,
      contact_email: contactData.contact_email.value?.toLowerCase(),
      contact_message: contactData.contact_message.value,
      contact_type_user: type
    }
  });
}

function getUserSession() {
  return fetchWrapper.get(`${config.apis.api.url}/user/me`, {
    injectToken: true
  });
}

function getUserPayments(userId) {
  if (!userId) {
    return Promise.resolve({ supportSubscriptions: [], paymentEntries: [] });
  }
  return fetchWrapper.get(`${config.apis.api.url}/user/${encodeURIComponent(userId)}/payments/`, {
    injectToken: true,
  });
}

// Created for sell in store - search collectors (most likely not needed)
function searchCollectors(searchTerm, { limit = 9, offset = 0 } = {}) {
  const params = new URLSearchParams({
    q: searchTerm ?? '',
  });

  if (limit !== undefined && limit !== null) {
    params.set('limit', limit);
  }

  if (offset !== undefined && offset !== null) {
    params.set('offset', offset);
  }

  const queryString = params.toString();

  return fetchWrapper.get(`${config.apis.api.url}/search/collector?${queryString}`, {
    injectToken: true,
  });
}

function createAccountAndCompleteOnboarding(accountData, user_type) {
  return fetchWrapper.put(`${config.apis.api.url}/${user_type}/onboarding/complete`, {
    body: accountData,
    injectToken: true
  });
}

function updateUserFlags(user_type, flags) {
  return fetchWrapper.put(`${config.apis.api.url}/${user_type}`, {
    body: flags,
    injectToken: true
  });
}

function updateUserPreferences(preferences) {
  return fetchWrapper.put(`${config.apis.api.url}/user/me`, {
    body: preferences,
    injectToken: true
  });
}

function uploadImage(image, onProgress) {
  return new Promise(resolve => {
    let imageURL;

    const failUpload = () => {
      if (imageURL) {
        URL.revokeObjectURL(imageURL);
        imageURL = null;
      }
      resolve({ response: { ok: false }, data: null });
    };

    try {
      imageURL = URL.createObjectURL(image);
      const imageElement = new Image();

      imageElement.onload = async function () {
        try {
          const imageWidth = this.width;
          const imageHeight = this.height;

          URL.revokeObjectURL(imageURL);
          imageURL = null;

          const { response, data } = await fetchWrapper.post(`${config.apis.api.url}/image/direct-upload`, {
            injectToken: true
          });

          if (!response.ok || !data?.result?.uploadURL) {
            return failUpload();
          }

          let bodyForCloudflare = new FormData();
          let fileToUpload = image;
          let finalWidth = imageWidth;
          let finalHeight = imageHeight;

          try {
            const prepared = await prepareImageForUpload(image, { sourceImage: this });
            if (prepared?.file) {
              fileToUpload = prepared.file;
              finalWidth = prepared.width || finalWidth;
              finalHeight = prepared.height || finalHeight;
            }
          } catch (prepError) {
            console.warn("prepareImageForUpload failed, uploading original file", prepError);
          }

          bodyForCloudflare.append("file", fileToUpload);

          const cloudflareResponse = await axios({
            method: "post",
            url: data.result.uploadURL,
            data: bodyForCloudflare,
            onUploadProgress: (progressEvent) => {
              if(onProgress) {
                onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
              }
            }
          });

          if(cloudflareResponse.data.success) {
            resolve({
              response: { ok: true }, 
              data: {
                image_id: cloudflareResponse.data.result.id,
                image_original_data: {
                  width: finalWidth,
                  height: finalHeight,
                  aspect_ratio: finalWidth / finalHeight,
                  name: fileToUpload.name
                }
              }
            });
          } else {
            failUpload();
          }
        } catch (error) {
          failUpload();
        }
      };

      imageElement.onerror = failUpload;
      imageElement.src = imageURL;  
    } catch(err) {
      failUpload();
    }
  });
}

function uploadAudio(audio, onProgress) {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve) => {
    try {
      const { response, data } = await fetchWrapper.post(`${config.apis.api.url}/audio/direct-upload`, {
        injectToken: true,
      });
        
      if(response.ok) {
        const cloudflareResponse = await axios({
          method: "put",
          url: data.url,
          data: audio,
          onUploadProgress: (progressEvent) => {
            if(onProgress) {
              onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
            }
          }
        });
    
        if(cloudflareResponse.status === 200) {
          resolve({
            response: { ok: true }, 
            data: {
              audio_id: data.id
            }
          });
        } else {
          throw new Error("Cloudflare upload failed");
        }
      } else {
        throw new Error("Upload URL request failed");
      }
    } catch(err) {
      resolve({ response: { ok: false }, data: null });
    }
  });
}

function followUser(userId, followeeType = 'user') {
  return fetchWrapper.post(`${config.apis.api.url}/user/follow`, {
    injectToken: true,
    body: {
      followee_id: userId,
      followee_type: followeeType,
    },
  });
}

function unfollowUser(userId, followeeType = 'user') {
  return fetchWrapper.post(`${config.apis.api.url}/user/unfollow`, {
    injectToken: true,
    body: {
      followee_id: userId,
      followee_type: followeeType,
    },
  });
}

function getFollowNotificationPreferences(userId) {
  return fetchWrapper.get(`${config.apis.api.url}/user/follow/preferences/${encodeURIComponent(userId)}`, {
    injectToken: true,
  });
}

function updateFollowNotificationPreferences(userId, notificationPreferences) {
  return fetchWrapper.put(`${config.apis.api.url}/user/follow/preferences/${encodeURIComponent(userId)}`, {
    injectToken: true,
    body: { notification_preferences: notificationPreferences },
  });
}

function createArtlist(newArtlist) {
  return fetchWrapper.post(`${config.apis.api.url}/artlist`, {
    injectToken: true,
    body: newArtlist,
  });
}

function editProfile(newProfileData) {
  return fetchWrapper.put(`${config.apis.api.url}/user/profile`, {
    injectToken: true,
    body: newProfileData,
  });
}

function changePassword(newPasswordData) {
  return fetchWrapper.put(`${config.apis.api.url}/user/password`, {
    injectToken: true,
    body: newPasswordData,
  });
}

function editArtlist(newArtlistData) {
  return fetchWrapper.put(`${config.apis.api.url}/artlist/${newArtlistData._id}`, {
    injectToken: true,
    body: {
      ...newArtlistData,
      artlist_artworks: newArtlistData.artlist_artworks.map(artwork => artwork._id)
    }
  });
}

function deleteArtlist(artlistId) {
  return fetchWrapper.delete(`${config.apis.api.url}/artlist/${artlistId}`, {
    injectToken: true,
  });
}

function addShippingAddress(addressData) {
  return fetchWrapper.post(`${config.apis.api.url}/address`, {
    injectToken: true,
    body: addressData,
  });
}

function editShippingAddress(addressData) {
  return fetchWrapper.put(`${config.apis.api.url}/address/${addressData._id}`, {
    injectToken: true,
    body: addressData,
  });
}

function deleteShippingAddress(addressData) {
  return fetchWrapper.delete(`${config.apis.api.url}/address/${addressData._id}`, {
    injectToken: true,
    body: addressData,
  });
}

// Persist the user-selected preferred shipping address.
function setPreferredShippingAddress(addressId) {
  const endpoint = `${config.apis.api.url}/address/preferred`;
  const payload = { address_id: addressId ?? null };

  return fetchWrapper.put(endpoint, {
    injectToken: true,
    body: payload,
  });
}

function editProduct(newProductData) {
  return fetchWrapper.put(`${config.apis.api.url}/product/${newProductData.product_id}`, {
    injectToken: true,
    body: newProductData,
  });
}

function likeItem(typeOfItem, itemId) {
  return fetchWrapper.post(`${config.apis.api.url}/${typeOfItem.toLowerCase()}/like/${itemId}`, {
    injectToken: true,
  });
}

function unlikeItem(typeOfItem, itemId) {
  return fetchWrapper.post(`${config.apis.api.url}/${typeOfItem.toLowerCase()}/unlike/${itemId}`, {
    injectToken: true,
  });
}

function saveItem(typeOfItem, itemId) {
  return fetchWrapper.post(`${config.apis.api.url}/${typeOfItem.toLowerCase()}/save/${itemId}`, {
    injectToken: true,
  });
}

function unsaveItem(typeOfItem, itemId) {
  return fetchWrapper.post(`${config.apis.api.url}/${typeOfItem.toLowerCase()}/unsave/${itemId}`, {
    injectToken: true,
  });
}

function addArtworkToArtlist(artwork, artlistId, artlistImagePayload = null) {
  const body = {
    artworks_add: [artwork]
  };

  if (artlistImagePayload) {
    body.artlist_image = artlistImagePayload;
  }

  return fetchWrapper.put(`${config.apis.api.url}/artlist/${artlistId}`, {
    injectToken: true,
    body
  });
}

function removeArtworkFromArtlist(artwork, artlistId) {
  return fetchWrapper.put(`${config.apis.api.url}/artlist/${artlistId}`, {
    injectToken: true,
    body: {
      artworks_remove: [artwork]
    }
  });
}

function submitRating(artworkId, myRating) {
  return fetchWrapper.post(`${config.apis.api.url}/rating/${artworkId}`, {
    injectToken: true,
    body: myRating,
  });
}

function publishThought(artworkId, messageOrPayload, parentId) {
  const body = { thought_parent: parentId };

  if (typeof messageOrPayload === 'string') {
    const trimmed = messageOrPayload.trim();
    if (trimmed) body.thought_message = trimmed;
  } else if (messageOrPayload && typeof messageOrPayload === 'object') {
    if (typeof messageOrPayload.text === 'string' && messageOrPayload.text.trim()) {
      body.thought_message = messageOrPayload.text.trim();
    }
    const stickerId = messageOrPayload.stickerId || messageOrPayload.sticker_id;
    if (stickerId) body.thought_sticker = stickerId;
  }

  if (!body.thought_message && !body.thought_sticker) {
    return Promise.resolve(null);
  }

  return fetchWrapper.post(`${config.apis.api.url}/thought/${artworkId}`, {
    injectToken: true,
    body,
  });
}

function deleteThought(thoughtId) {
  return fetchWrapper.delete(`${config.apis.api.url}/thought/${thoughtId}/`, {
    injectToken: true
  });
}

function createArtworkPaymentIntent(artworkId, purchaseData, typeOfPurchase) {
  return fetchWrapper.post(`${config.apis.api.url}/payment/intent`, {
    injectToken: true,
    body: {
      ...purchaseData,
      artwork_id: artworkId,
      type_of_purchase: typeOfPurchase
    },
  });
}

function getArtworkPaymentIntentStatus(paymentIntentId) {
  return fetchWrapper.get(`${config.apis.api.url}/payment/intent/${paymentIntentId}/status`, {
    injectToken: true,
  });
}

function getLimitedEditionShippingQuote({ country, size } = {}) {
  const searchParams = new URLSearchParams();
  if (country) {
    searchParams.set('country', country);
  }
  if (size) {
    searchParams.set('size', size);
  }
  const query = searchParams.toString();
  const endpoint = `${config.apis.api.url}/payment/limited-edition/shipping-quote${query ? `?${query}` : ''}`;
  return fetchWrapper.get(endpoint, {
    injectToken: true,
  });
}

function submitReview(galleryId, reviewData) {
  return fetchWrapper.post(`${config.apis.api.url}/review`, {
    injectToken: true,
    body: {
      gallery_id: galleryId,
      ...reviewData
    },
  });
}

function confirmReception(orderNumber, review) {
  return fetchWrapper.put(`${config.apis.api.url}/order/${orderNumber}/confirm-reception`, {
    injectToken: true,
    body: {
      review
    }
  });
}

function cancelOrder(orderNumber, payload) {
  return fetchWrapper.post(`${config.apis.api.url}/orders/${orderNumber}/cancel`, {
    injectToken: true,
    body: payload,
  });
}

function reportIssue(orderId, issueData) {
  return fetchWrapper.post(`${config.apis.api.url}/issue/new/${orderId}`, {
    injectToken: true,
    body: issueData
  });
}

function resolveIssue(orderId) {
  return fetchWrapper.post(`${config.apis.api.url}/issue/close/${orderId}`, {
    injectToken: true,
  });
}

function sendMessage(conversationId, messageData) {
  let payload = {};

  if(typeof messageData === 'string') {
    const trimmed = messageData.trim();
    if(trimmed.length > 0) {
      payload.message_text = trimmed;
    }
  } else if(messageData && typeof messageData === 'object') {
    if(typeof messageData.text === 'string' && messageData.text.trim().length > 0) {
      payload.message_text = messageData.text.trim();
    }

    const stickerId = messageData.stickerId || messageData.sticker_id;
    if(stickerId) {
      payload.message_sticker = stickerId;
    }
  }

  if(!payload.message_text && !payload.message_sticker) {
    return Promise.resolve(null);
  }

  return fetchWrapper.post(`${config.apis.api.url}/order/${conversationId}/chat`, {
    injectToken: true,
    body: payload
  });
}

// Function for getting a DM conversation, mirroring getChannelConversation
function getChatConversation(user_username) {
  return fetchWrapper.get(`${config.apis.api.url}/chat/dm/${user_username}`, {
    injectToken: true
  });
}

async function postChatMessage(user_username, messageText) {
  return fetchWrapper.post(
    `${config.apis.api.url}/chat/dm/${user_username}`,
    { 
      injectToken: true,
      body: {
        message_text: messageText
      },
    }
  );
}

// Added getUserChats function for fetching direct messages (chats)
function getUserChats() {
  return fetchWrapper.get(`${config.apis.api.url}/chat/directmessages`, {
    injectToken: true
  });
}

function getUserChatsUnreadSummary() {
  if (!userChatsUnreadSummaryRequest) {
    userChatsUnreadSummaryRequest = fetchWrapper
      .get(`${config.apis.api.url}/chat/directmessages/unread-summary`, {
        injectToken: true
      })
      .finally(() => {
        userChatsUnreadSummaryRequest = null;
      });
  }

  return userChatsUnreadSummaryRequest;
}

function getUserStickers(user_username) {
  return fetchWrapper.get(`${config.apis.api.url}/profile/${user_username}/my-stickers`, {
    injectToken: true
  });
}

function getAllUserStickers(user_username = 'me') {
  return fetchWrapper.get(`${config.apis.api.url}/profile/${user_username}/all-stickers`, {
    injectToken: true
  });
}

function createUserSticker(user_username, { imageId, cropShape }) {
  return fetchWrapper.post(`${config.apis.api.url}/profile/${user_username}/my-stickers`, {
    injectToken: true,
    body: { imageId, cropShape },
  });
}

function deleteUserSticker(user_username, stickerId) {
  return fetchWrapper.delete(`${config.apis.api.url}/profile/${user_username}/my-stickers/${stickerId}`, {
    injectToken: true,
  });
}

function claimArtwork(claimCode) {
  return fetchWrapper.post(`${config.apis.api.url}/claim`, {
    injectToken: true,
    body: {
      claim_code: claimCode
    }
  });
}

function notifyWhenGalleryOpens(artworkId) {
  return fetchWrapper.post(`${config.apis.api.url}/artwork/${artworkId}/notify-me`, {
    injectToken: true
  });
}

function notifyChromaticMusicLaunch() {
  return fetchWrapper.post(`${config.apis.api.url}/art-games/chromatic-music/notify-me`, {
    injectToken: true,
  });
}

function submitColorMixRound(roundData) {
  return fetchWrapper.post(`${config.apis.api.url}/art-games/color-mix/rounds`, {
    injectToken: true,
    body: roundData,
  });
}

function saveIppitsugakiPiece(pieceData) {
  return fetchWrapper.post(`${config.apis.api.url}/art-games/ippitsugaki/pieces`, {
    injectToken: true,
    body: pieceData,
  });
}

function listIppitsugakiPieces() {
  return fetchWrapper.get(`${config.apis.api.url}/art-games/ippitsugaki/pieces`, {
    injectToken: true,
  });
}

function deleteIppitsugakiPiece(pieceId) {
  return fetchWrapper.delete(`${config.apis.api.url}/art-games/ippitsugaki/pieces/${encodeURIComponent(pieceId)}`, {
    injectToken: true,
  });
}

function registerIppitsugakiDownload() {
  return fetchWrapper.post(`${config.apis.api.url}/art-games/ippitsugaki/download`, {
    injectToken: true,
  });
}

function saveKaleidoscopePattern(patternData) {
  return fetchWrapper.post(`${config.apis.api.url}/art-games/kaleidoscope/patterns`, {
    injectToken: true,
    body: patternData,
  });
}

function listKaleidoscopePatterns() {
  return fetchWrapper.get(`${config.apis.api.url}/art-games/kaleidoscope/patterns`, {
    injectToken: true,
  });
}

function deleteKaleidoscopePattern(patternId) {
  return fetchWrapper.delete(`${config.apis.api.url}/art-games/kaleidoscope/patterns/${encodeURIComponent(patternId)}`, {
    injectToken: true,
  });
}

function registerKaleidoscopeDownload() {
  return fetchWrapper.post(`${config.apis.api.url}/art-games/kaleidoscope/download`, {
    injectToken: true,
  });
}

function initChromaticMusicExport(payload) {
  return fetchWrapper.post(`${config.apis.api.url}/api/v1/exports/init`, {
    injectToken: true,
    body: payload,
  });
}

function completeChromaticMusicExport(payload) {
  return fetchWrapper.post(`${config.apis.api.url}/api/v1/exports/complete`, {
    injectToken: true,
    body: payload,
  });
}

function getChromaticMusicExportStatus(jobId) {
  return fetchWrapper.get(`${config.apis.api.url}/api/v1/exports/${encodeURIComponent(jobId)}`, {
    injectToken: true,
  });
}

function getColorMixLeaderboard(limit = 100) {
  const searchParams = new URLSearchParams();
  if (limit !== undefined && limit !== null) {
    searchParams.set('limit', limit);
  }

  const query = searchParams.toString();
  const url = `${config.apis.api.url}/art-games/color-mix/leaderboard${query ? `?${query}` : ''}`;

  return fetchWrapper.get(url, {
    injectToken: true,
  });
}

function deleteAccount() {
  return fetchWrapper.delete(`${config.apis.api.url}/user`, {
    injectToken: true
  });
}

const UserService = {
  contact,
  getUserSession,
  createAccountAndCompleteOnboarding,
  updateUserFlags,
  updateUserPreferences,
  uploadImage,
  uploadAudio,
  followUser,
  unfollowUser,
  getFollowNotificationPreferences,
  updateFollowNotificationPreferences,
  createArtlist,
  editProfile,
  changePassword,
  editArtlist,
  deleteArtlist,
  addShippingAddress,
  editShippingAddress,
  deleteShippingAddress,
  setPreferredShippingAddress,
  editProduct,
  likeItem,
  unlikeItem,
  saveItem,
  unsaveItem,
  addArtworkToArtlist,
  removeArtworkFromArtlist,
  submitRating,
  publishThought,
  createArtworkPaymentIntent,
  getArtworkPaymentIntentStatus,
  getLimitedEditionShippingQuote,
  submitReview,
  confirmReception,
  cancelOrder,
  reportIssue,
  resolveIssue,
  sendMessage,
  claimArtwork,
  notifyWhenGalleryOpens,
  notifyChromaticMusicLaunch,
  submitColorMixRound,
  saveIppitsugakiPiece,
  listIppitsugakiPieces,
  deleteIppitsugakiPiece,
  registerIppitsugakiDownload,
  saveKaleidoscopePattern,
  listKaleidoscopePatterns,
  deleteKaleidoscopePattern,
  registerKaleidoscopeDownload,
  initChromaticMusicExport,
  completeChromaticMusicExport,
  getChromaticMusicExportStatus,
  getColorMixLeaderboard,
  deleteAccount,
  getUserPayments,
  getChatConversation,
  postChatMessage,
  getUserChats,
  getUserChatsUnreadSummary,
  getUserStickers,
  getAllUserStickers,
  createUserSticker,
  deleteUserSticker,
  deleteThought,
  searchCollectors
};

export default UserService;
